import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { LEAD_STATUSES, STATUS_STYLE, STATUS_DOT, fmtMoney } from "@/lib/leadConstants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Phone, Mail, MapPin, BookOpen, CalendarClock, Plus, CheckCircle2,
  Circle, Activity, User, Clock,
} from "lucide-react";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="break-words text-sm text-secondary">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function LeadDetailsSheet({ leadId, open, onOpenChange, executives, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [fuOpen, setFuOpen] = useState(false);
  const [fu, setFu] = useState({ title: "", notes: "", scheduled_date: "", scheduled_time: "" });
  const [savingFu, setSavingFu] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!leadId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { if (open && leadId) load(); }, [open, leadId, load]);

  const lead = data?.lead;

  const changeStatus = async (status) => {
    await api.patch(`/leads/${leadId}/status`, { status });
    toast.success(`Status updated to ${status}`);
    load(); onChanged?.();
  };

  const assign = async (assigned_to) => {
    const val = assigned_to === "unassigned" ? "" : assigned_to;
    await api.patch(`/leads/${leadId}/assign`, { assigned_to: val });
    toast.success("Assignment updated");
    load(true); onChanged?.();
  };

  const addFollowup = async (e) => {
    e.preventDefault();
    if (!fu.title.trim()) { toast.error("Follow-up title is required"); return; }
    setSavingFu(true);
    try {
      await api.post(`/leads/${leadId}/followups`, fu);
      toast.success("Follow-up added");
      setFu({ title: "", notes: "", scheduled_date: "", scheduled_time: "" });
      setFuOpen(false);
      load(true); onChanged?.();
    } finally {
      setSavingFu(false);
    }
  };

  const completeFollowup = async (fid) => {
    await api.patch(`/leads/${leadId}/followups/${fid}/complete`);
    toast.success("Follow-up marked complete");
    load(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="lead-details-sheet">
        {loading || !lead ? (
          <div className="flex h-full items-center justify-center">
            <SheetTitle className="sr-only">Lead details</SheetTitle>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <SheetHeader className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-400">{lead.lead_code}</span>
                <Badge className={`${STATUS_STYLE[lead.status]} border-0`}>{lead.status}</Badge>
              </div>
              <SheetTitle className="text-xl">{lead.full_name}</SheetTitle>
              <SheetDescription>{lead.interested_course || "No course selected"}</SheetDescription>
            </SheetHeader>

            {/* Quick actions */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={lead.status} onValueChange={changeStatus}>
                  <SelectTrigger data-testid="details-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Assigned To</Label>
                <Select value={lead.assigned_to || "unassigned"} onValueChange={assign}>
                  <SelectTrigger data-testid="details-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {executives.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-5">
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="followups" className="flex-1" data-testid="tab-followups">Follow-ups</TabsTrigger>
                <TabsTrigger value="activity" className="flex-1" data-testid="tab-activity">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4">
                <div className="divide-y divide-border rounded-lg border border-border px-4">
                  <Row icon={Phone} label="Mobile" value={lead.mobile} />
                  <Row icon={Mail} label="Email" value={lead.email} />
                  <Row icon={MapPin} label="Location" value={[lead.city, lead.state].filter(Boolean).join(", ")} />
                  <Row icon={BookOpen} label="Preferred Batch" value={lead.preferred_batch} />
                  <Row icon={CalendarClock} label="Expected Joining" value={lead.expected_joining_month} />
                  <Row icon={User} label="Source" value={lead.source} />
                  <Row icon={Clock} label="Budget" value={lead.budget ? fmtMoney(lead.budget) : ""} />
                  <Row icon={CalendarClock} label="Follow-up" value={[lead.follow_up_date, lead.next_follow_up_time].filter(Boolean).join(" ")} />
                </div>
                {lead.remarks && (
                  <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                    <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Remarks</p>
                    {lead.remarks}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="followups" className="mt-4">
                {!fuOpen ? (
                  <Button variant="outline" className="mb-4 w-full" onClick={() => setFuOpen(true)} data-testid="add-followup-button">
                    <Plus className="mr-1.5 h-4 w-4" /> Add Follow-up
                  </Button>
                ) : (
                  <form onSubmit={addFollowup} className="mb-4 space-y-3 rounded-lg border border-border p-3" data-testid="followup-form">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Title *</Label>
                      <Input value={fu.title} onChange={(e) => setFu({ ...fu, title: e.target.value })} placeholder="e.g. Call back to discuss fees" data-testid="followup-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Date</Label>
                        <Input type="date" value={fu.scheduled_date} onChange={(e) => setFu({ ...fu, scheduled_date: e.target.value })} data-testid="followup-date-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Time</Label>
                        <Input type="time" value={fu.scheduled_time} onChange={(e) => setFu({ ...fu, scheduled_time: e.target.value })} data-testid="followup-time-input" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Notes</Label>
                      <Textarea rows={2} value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} data-testid="followup-notes-input" />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setFuOpen(false)}>Cancel</Button>
                      <Button type="submit" size="sm" disabled={savingFu} data-testid="save-followup-button">
                        {savingFu ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-3" data-testid="followup-timeline">
                  {data.followups.length === 0 && <p className="text-sm text-muted-foreground">No follow-ups yet.</p>}
                  {data.followups.map((f) => {
                    const done = f.status === "completed";
                    return (
                      <div key={f.id} className="flex gap-3 rounded-lg border border-border p-3" data-testid={`followup-${f.id}`}>
                        <button onClick={() => !done && completeFollowup(f.id)} disabled={done} data-testid={`complete-followup-${f.id}`} className="mt-0.5">
                          {done ? <CheckCircle2 className="h-5 w-5 text-accent" /> : <Circle className="h-5 w-5 text-slate-300 hover:text-primary" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${done ? "text-muted-foreground line-through" : "text-secondary"}`}>{f.title}</p>
                          {f.notes && <p className="text-xs text-muted-foreground">{f.notes}</p>}
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                            {f.scheduled_date && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {f.scheduled_date} {f.scheduled_time}</span>}
                            <span>· by {f.created_by_name}</span>
                            {done && <Badge className="border-0 bg-accent/15 text-[10px] text-green-700">Completed</Badge>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <div className="space-y-4" data-testid="activity-timeline">
                  {data.activities.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
                  {data.activities.map((a) => (
                    <div key={a.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
                          <Activity className="h-3.5 w-3.5 text-primary" />
                        </span>
                        <span className="my-1 w-px flex-1 bg-border" />
                      </div>
                      <div className="min-w-0 pb-2">
                        <p className="text-sm text-secondary">{a.description}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium" style={{ color: STATUS_DOT[lead.status] }}>{a.type}</span> · {a.user_name} · {new Date(a.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
